import { useDashboardSpotlight } from '../hooks/useDashboardSpotlight';
import { Skeleton } from './ui/Skeleton';
import type { SpotlightAccent } from '../types/dashboard.types';

const ACCENT_STYLES: Record<SpotlightAccent, string> = {
  violet: 'vania-spotlight-card--violet',
  teal: 'vania-spotlight-card--teal',
  amber: 'vania-spotlight-card--amber',
  rose: 'vania-spotlight-card--rose',
  sky: 'vania-spotlight-card--sky',
};

/** Placeholder shaped like the real spotlight card, not a generic spinner box. */
function SpotlightSkeleton() {
  return (
    <div
      className="vania-glass-card vania-spotlight-card"
      role="status"
      aria-live="polite"
      aria-label="در حال بارگذاری اسپات‌لایت"
      data-animate="fade-in"
    >
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-3.5 w-1/3" />
      <Skeleton className="h-3.5 w-2/3" />
      <Skeleton className="h-9 w-32 mt-2" rounded="lg" />
    </div>
  );
}

export function DashboardSpotlight() {
  const { spotlight, isInitialLoading, error, retry } = useDashboardSpotlight();

  // Cache-first: only the "nothing to show yet" state renders a skeleton.
  // A background refresh behind an already-visible card is silent.
  if (isInitialLoading) {
    return <SpotlightSkeleton />;
  }

  if (error) {
    return (
      <div
        className="vania-glass-card vania-glass-card--elevated vania-spotlight-card vania-spotlight-card--error !flex-row items-center justify-between gap-4"
        data-animate="fade-in"
      >
        <span className="text-sm text-[var(--vania-accent-rose,#ff6b81)]">
          بارگذاری اسپات‌لایت با خطا مواجه شد.
        </span>
        <button
          type="button"
          onClick={retry}
          className="vania-spotlight-retry rounded-lg border border-[var(--vania-border-glass,rgba(255,255,255,0.12))] px-3 py-1.5 text-sm font-medium text-[var(--vania-text-primary,#f5f6fa)] hover:bg-[var(--vania-surface-2,rgba(255,255,255,0.05))]"
        >
          تلاش دوباره
        </button>
      </div>
    );
  }

  // No error, not loading, and either the backend reported no active
  // spotlight or the caller isn't authenticated yet — nothing to render.
  if (!spotlight || !spotlight.active) {
    return null;
  }

  const accentClass = ACCENT_STYLES[spotlight.accent];

  return (
    <div
      className={`vania-glass-card vania-glass-card--elevated vania-spotlight-card ${accentClass}`}
      data-animate="fade-in"
    >
      <h2 className="vania-spotlight-title">{spotlight.title}</h2>

      {spotlight.subtitle ? <p className="vania-spotlight-subtitle">{spotlight.subtitle}</p> : null}

      {spotlight.message ? <p className="vania-spotlight-message">{spotlight.message}</p> : null}

      {spotlight.action ? (
        <a
          href={spotlight.action.url}
          className="vania-spotlight-cta mt-2 inline-flex w-fit items-center rounded-lg px-4 py-2 text-sm font-medium"
        >
          {spotlight.action.label}
        </a>
      ) : null}
    </div>
  );
}
